import { AddOutlined, AutoAwesomeOutlined, CalendarTodayOutlined, ChevronRightOutlined, ExpandMoreOutlined, FormatListBulletedOutlined } from '@mui/icons-material'
import { CalendarSlot, CampaignCalendar } from './CampaignCalendar.component'
import { CampaignPhase, CampaignPostDTO, SocialCampaignDTO } from 'models/socialCampaign'
import { FC, KeyboardEvent, useId, useMemo, useState } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { formatWeekRange, groupPostsByWeek, splitSchedule, weekRelationOf } from 'utils/socialCampaign/schedule'

import { CampaignPostRow } from './CampaignPostRow.component'
import { EmptyContent } from 'components/common/EmptyContent'
import { FilterTab } from 'components/common/FilterTab'
import { MUIButton } from 'components/common/MUIButton'
import { SectionHead } from 'components/common/SectionHead'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { campaignDayOf } from 'utils/socialCampaign/campaignStatus'
import classnames from 'classnames'
import planStyles from './CampaignPlanAndContent.module.sass'
import styles from './CampaignSchedule.module.sass'
import { useTranslation } from 'react-i18next'

/** The list is the schedule's main view, the week calendar its second (CTO, 7 Oct 2026) */
export type ScheduleView = 'list' | 'calendar'

interface Props {
  campaign: SocialCampaignDTO
  now: Date
  /**
   * A plan or kind change of section 1 is waiting or on its way: the drafts are being re-planned, the list is dimmed
   * and busy until the backend answered.
   */
  saving: boolean
  /** A change or an action is on its way: "Generate new posts" waits, as the action bar's does */
  busy?: boolean
  /** Opens a post in the editor (ISSUE-3624); the rows are static content without it */
  onOpenPost?: (postId: string) => void
  /** Quiet: "Generate new posts" in the empty state */
  onGenerate: () => void
  /** Quiet: "Add a post" in the empty state (ISSUE-3624 opens the editor); disabled without it */
  onAddPost?: () => void
  /** The calendar's free hour: opens the editor in Create mode on that day and hour; no free hour without it */
  onAddPostAt?: (slot: CalendarSlot) => void
  /** The view shown, when the page holds it (it goes back to the list after "Generate new posts"); else held here */
  view?: ScheduleView
  onViewChange?: (view: ScheduleView) => void
}

/** A view tab: compact beside the title, sharing the width on a phone */
const VIEW_TAB_SX = { flex: { xs: '1 1 0', sm: 'none' }, justifyContent: 'center', height: { xs: '4rem', sm: '3.6rem' } }

/** The view tabs: arrow keys, Home and End move between them (the selected one keeps the tab stop) */
const moveBetweenTabs = (event: KeyboardEvent) => {
  const tabs = Array.from(event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [])
  const index = tabs.indexOf(event.currentTarget as HTMLElement)
  const next = {
    ArrowRight: (index + 1) % tabs.length,
    ArrowLeft: (index - 1 + tabs.length) % tabs.length,
    Home: 0,
    End: tabs.length - 1,
  }[event.key]
  if (next === undefined || index < 0) return
  event.preventDefault()
  tabs[next].focus()
}

/**
 * Section 2 of the campaign, the Schedule (ISSUE-3623, prototype v3 `scheduleSection` and `listHtml`): the list, its
 * posts by week. Numbered 2 on a draft, under Plan & content; once launched the page is the schedule, and the list
 * opens on what comes next: what went out folds into one line above it ("5 posts published · Show"), which opens
 * inside itself. An ended campaign shows everything. Quiet, an empty state invites to generate or add a post. The
 * List / Calendar tabs show the week calendar instead (ISSUE-3649, `CampaignCalendar`).
 */
export const CampaignSchedule: FC<Props> = ({ campaign, now, saving, busy = false, onOpenPost, onGenerate, onAddPost, onAddPostAt, view: viewShown, onViewChange }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const ids = `campaign-schedule-${useId().replace(/:/g, '')}`
  const [showPast, setShowPast] = useState(false)
  const [ownView, setOwnView] = useState<ScheduleView>('list')
  const view = viewShown ?? ownView
  const showView = (next: ScheduleView) => {
    setOwnView(next)
    onViewChange?.(next)
  }
  // The selected tab keeps the tab stop and names the one panel rendered
  const tabOf = (tab: ScheduleView) => ({
    role: 'tab' as const,
    id: `${ids}-${tab}-tab`,
    ariaSelected: view === tab,
    ariaControls: view === tab ? `${ids}-${tab}` : undefined,
    tabIndex: view === tab ? 0 : -1,
    isActive: view === tab,
    onClick: () => showView(tab),
    onKeyDown: moveBetweenTabs,
    sx: VIEW_TAB_SX,
  })
  const draft = campaign.phase === CampaignPhase.DRAFT
  const quiet = campaign.phase === CampaignPhase.QUIET

  const split = useMemo(() => splitSchedule(campaign, now), [campaign, now])
  const today = campaignDayOf(now.toISOString(), campaign.timezone)

  const weeks = (posts: CampaignPostDTO[]) => groupPostsByWeek(posts, campaign.timezone).map((week, index, all) => {
    const range = formatWeekRange(week.monday, i18n.language)
    return (
      <div key={week.monday} className={styles.week}>

        <Typography
          variant="text-sm"
          component="h4"
          className={styles.weekTitle}
          // `&&` outranks the old-style routes' global h1-h4 sizes and colour (src/styles/index.sass)
          sx={theme => ({ '&&': { margin: 0, fontSize: theme.typography['text-sm']?.fontSize, lineHeight: theme.typography['text-sm']?.lineHeight, fontWeight: 550, color: GRAY_900 } })}
        >
          {t(`schedule.week.${weekRelationOf(week.monday, today)}`, { range })}
        </Typography>

        <ul className={classnames(styles.rows, { [styles.joinsNext]: index < all.length - 1 })}>
          {week.posts.map((post) => <CampaignPostRow key={post.id} post={post} campaign={campaign} now={now} onOpen={onOpenPost} />)}
        </ul>

      </div>
    )
  })

  const foldSummary = [
    ...(split.published ? [t('schedule.fold.published', { count: split.published })] : []),
    ...(split.notPublished ? [t('schedule.fold.not_published', { count: split.notPublished })] : []),
  ].join(' · ')

  return (
    <section className={classnames(planStyles.card, styles.schedule)} aria-labelledby={`${ids}-title`} data-saving={saving || undefined}>

      <SectionHead
        id={`${ids}-title`}
        titleComponent="h3"
        isDivided
        title={<span className={planStyles.headTitle}>{draft && <span className={planStyles.num} aria-hidden="true">2</span>}{t('schedule.title')}</span>}
        trailing={
          <Stack direction="row" gap=".6rem" role="tablist" aria-label={t('schedule.view.label')} sx={{ width: { xs: '100%', sm: 'auto' } }}>

            <FilterTab {...tabOf('list')} label={t('schedule.view.list')} icon={<FormatListBulletedOutlined sx={{ fontSize: '1.6rem' }} />} />

            <FilterTab {...tabOf('calendar')} label={t('schedule.view.calendar')} icon={<CalendarTodayOutlined sx={{ fontSize: '1.6rem' }} />} />

          </Stack>
        }
      />

      <Stack id={`${ids}-${view}`} role="tabpanel" aria-labelledby={`${ids}-${view}-tab`} gap="2rem">

        {quiet &&
          <EmptyContent
            titleComponent="h4"
            icon={<CalendarTodayOutlined sx={{ fontSize: '2.4rem', color: GRAY_700 }} />}
            title={t('schedule.empty.title')}
            description={t('schedule.empty.body')}
            button={
              <Stack direction="row" flexWrap="wrap" justifyContent="center" gap=".8rem" marginTop="1.6rem">

                <MUIButton startIcon={<AutoAwesomeOutlined />} disabled={busy} onClick={onGenerate}>{t('bar.generate')}</MUIButton>

                <MUIButton type="secondaryBorder" startIcon={<AddOutlined />} disabled={!onAddPost} onClick={onAddPost}>{t('bar.add_post')}</MUIButton>

              </Stack>
            }
          />
        }

        {view === 'calendar' &&
          <div className={styles.calendar} aria-busy={saving || undefined}>
            <CampaignCalendar campaign={campaign} now={now} onOpenPost={onOpenPost} onAddPostAt={onAddPostAt} />
          </div>
        }

        {view === 'list' && campaign.posts.length === 0 && <Typography variant="text-sm" component="p" color={GRAY_700}>{t('schedule.no_posts')}</Typography>}

        {view === 'list' && campaign.posts.length > 0 &&
          <div className={styles.list} aria-busy={saving || undefined}>

            {split.folded.length > 0 &&
              <div className={styles.past} data-open={showPast || undefined}>

                <button
                  type="button"
                  className={styles.pastToggle}
                  aria-expanded={showPast}
                  // the list is in the page only while open
                  aria-controls={showPast ? `${ids}-past` : undefined}
                  onClick={() => setShowPast((shown) => !shown)}
                >
                  <span className={styles.pastChevron} aria-hidden="true">
                    {showPast ? <ExpandMoreOutlined sx={{ fontSize: '2rem' }} /> : <ChevronRightOutlined sx={{ fontSize: '2rem' }} />}
                  </span>
                  <span>{foldSummary}</span>
                  <span className={styles.pastAction}>{showPast ? t('schedule.fold.hide') : t('schedule.fold.show')}</span>
                </button>

                {showPast && <div className={classnames(styles.list, styles.pastList)} id={`${ids}-past`}>{weeks(split.folded)}</div>}

              </div>
            }

            {weeks(split.shown)}

          </div>
        }

      </Stack>

    </section>
  )
}
